import React, { useEffect, useState, useCallback } from "react";
import { Add } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { toast } from "react-toastify";

import FormDrawer from "components/Shared/FormDrawer";
import ShipmentPricingForm from "./ShipmentPricingForm";
import ShipmentPricingList from "./ShipmentPricingList";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import {
  useAddShipmentPriceMutation,
  useLazyListShipmentPricesQuery,
  useUpdateShipmentPriceMutation,
  useGenerateShipmentEBillMutation,
  usePrintShipmentPriceInvoiceMutation,
  usePrintShipmentStoredEBillMutation,
  usePrintShipmentPriceCreditNoteMutation,
} from "store/services/shipmentApi";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useAuth } from "hooks";
import { useListListQuery } from "store/services/listService";

const ShipmentPricesTab = () => {
  const { isCustomer, isVendor, isContractor, isDriver, isVendorStaff, isAccountAdmin } =
    useAuth();
  const canEdit =
    isCustomer || isVendor || isContractor || isDriver || isVendorStaff;
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const { ShipmentDetail } = useShipmentDetailContext();
  const shipmentId = ShipmentDetail?._id;

  const [fetchPrices, { data: pricesRes, error }] =
    useLazyListShipmentPricesQuery();
  const [addPrice, { isLoading: addingPrice }] = useAddShipmentPriceMutation();
  const [bulkInProgress, setBulkInProgress] = useState(false);
  const [updatePrice, { isLoading: updatingPrice }] =
    useUpdateShipmentPriceMutation();
  const [generateEBill] = useGenerateShipmentEBillMutation();
  const [printEBill, { isLoading: printingEBill }] = usePrintShipmentPriceInvoiceMutation();
  const [printStoredEBill, { isLoading: printingStoredEBill }] = usePrintShipmentStoredEBillMutation();
  const [printCreditNote, { isLoading: printingCreditNote }] =
    usePrintShipmentPriceCreditNoteMutation();
  const [printAnchor, setPrintAnchor] = useState(null);

  const { data: feeTypeList = [], isLoading: loadingFeeTypes } =
    useListListQuery(
      {
        type: ENTITY_LIST_CONSTANTS.SHIPMENT_FEES,
        status: "Active",
      },
      { skip: !shipmentId }
    );
  const _mappedFeeTypeList = React.useMemo(
    () =>
      Array.isArray(feeTypeList)
        ? feeTypeList.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
        : [],
    [feeTypeList]
  );

  const { data: discountTypeList = [], isLoading: loadingDiscountTypes } =
    useListListQuery(
      {
        type: ENTITY_LIST_CONSTANTS.SHIPMENT_DISCOUNT,
        status: "Active",
      },
      { skip: !shipmentId }
    );
  const _mappedDiscountTypeList = React.useMemo(
    () =>
      Array.isArray(discountTypeList)
        ? discountTypeList.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
        : [],
    [discountTypeList]
  );

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Error loading shipment prices");
    }
  }, [error]);

  const refreshPrices = useCallback(() => {
    if (!shipmentId) {
      toast.error("Shipment ID is missing");
      return;
    }
    fetchPrices({ shipmentId, page: 1, limit: 1000 }).catch(() => {
      toast.error("Failed to load shipment prices");
    });
  }, [shipmentId, fetchPrices]);

  const eBills = React.useMemo(() => {
    const unique = new Map();
    (pricesRes?.data || []).forEach((price) => {
      if (price?.eBillId && !unique.has(price.eBillId)) {
        unique.set(price.eBillId, { ...price, storedEBill: false });
      }
    });
    (ShipmentDetail?.eBills || []).forEach((bill) => {
      if (bill?.eBillId && !unique.has(bill.eBillId)) {
        unique.set(bill.eBillId, {
          ...bill,
          _id: shipmentId,
          storedEBill: true,
        });
      }
    });
    return [...unique.values()];
  }, [pricesRes?.data, ShipmentDetail?.eBills, shipmentId]);

  const creditNotes = React.useMemo(() => {
    const unique = new Map();
    (pricesRes?.data || []).forEach((price) => {
      (price?.creditNotes || []).forEach((creditNote) => {
        if (creditNote?.creditNoteId && !unique.has(creditNote.creditNoteId)) {
          unique.set(creditNote.creditNoteId, {
            ...creditNote,
            priceId: price._id,
            payer: price.payer,
          });
        }
      });
    });
    return [...unique.values()];
  }, [pricesRes?.data]);

  const handlePrintEBill = async (priceId) => {
    try {
      const response = await printEBill(priceId).unwrap();
      const printUrl = response?.eBillPrintUrl || response?.data?.print_url || response?.data?.printUrl || response?.data?.url;
      if (!printUrl) throw new Error("No eBill print URL returned");
      window.open(printUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.data?.message || err.message || "eBill print failed");
    }
    setPrintAnchor(null);
  };

  const handlePrintStoredEBill = async (eBillId) => {
    try {
      const response = await printStoredEBill({ shipmentId, eBillId }).unwrap();
      const printUrl = response?.eBillPrintUrl || response?.data?.print_url || response?.data?.printUrl || response?.data?.url;
      if (!printUrl) throw new Error("No eBill print URL returned");
      window.open(printUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.data?.message || err.message || "eBill print failed");
    }
    setPrintAnchor(null);
  };

  const handlePrintCreditNote = async (creditNote) => {
    try {
      const response = await printCreditNote({
        priceId: creditNote.priceId,
        creditNoteId: creditNote.creditNoteId,
      }).unwrap();
      const printUrl =
        response?.creditNotePrintUrl ||
        response?.data?.print_url ||
        response?.data?.printUrl ||
        response?.data?.url;
      if (!printUrl) throw new Error("No credit note print URL returned");
      window.open(printUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.data?.message || err.message || "Credit note print failed");
    }
    setPrintAnchor(null);
  };

  useEffect(() => {
    if (shipmentId) {
      refreshPrices();
    }
  }, [shipmentId, refreshPrices]);

  const handleCancelPrice = useCallback(() => {
    setShowPriceForm(false);
    setSelectedPrice(null);
  }, []);

  const handleAddPrice = useCallback(
    async (data) => {
      try {
        const targetShipmentId = shipmentId || ShipmentDetail?._id;
        if (!targetShipmentId) {
          toast.error("Shipment ID is missing");
          return;
        }

        const rawItems = Array.isArray(data?.items) ? data.items : [data];
        const hasCharge = rawItems.some((item) => item.chargeType === "Charge");

        // Build cleaned items — feeItem omitted for COD
        const items = rawItems.map((item) => ({
          ...(item.chargeType !== "Cash on Delivery" && item.feeItem
            ? { feeItem: item.feeItem }
            : {}),
          responsibleUser: item.responsibleUser || null,
          responsibleUserType: item.responsibleUserType,
          charge: item.charge,
          chargeType: item.chargeType,
          payer: item.payer,
          paymentLocation: item.paymentLocation,
          ...(item.chargeType === "Charge" || item.chargeType === "Cash on Delivery"
            ? { paymentStatus: item.paymentStatus }
            : { discountStatus: item.discountStatus }),
          deductFromCOD: item.deductFromCOD || null,
        }));

        // Close drawer first, then show full-screen progress overlay
        handleCancelPrice();
        setBulkInProgress(true);

        // Single request — server handles all items in one transaction
        await addPrice({ shipmentId: targetShipmentId, items }).unwrap();

        if (hasCharge) {
          try {
            await generateEBill(targetShipmentId).unwrap();
            toast.success("Price and sales eBill created successfully.");
          } catch (eBillErr) {
            toast.error(
              eBillErr?.data?.message ||
                "Price was saved, but the sales eBill could not be created. Please retry eBill generation."
            );
          }
        }

        toast.success(
          items.length === 1
            ? "Price successfully added!"
            : `${items.length} prices successfully added!`
        );
      } catch (err) {
        console.error("Add price error:", err);
        toast.error(err?.data?.message ?? "Couldn't add price(s)!");
      } finally {
        setBulkInProgress(false);
      }
    },
    [shipmentId, ShipmentDetail?._id, addPrice, generateEBill, handleCancelPrice]
  );

  const handleUpdatePrice = useCallback(
    async (data) => {
      try {
        const resolvedShipmentId = data.shipmentId || shipmentId || ShipmentDetail?._id;
        if (!resolvedShipmentId) {
          toast.error("Shipment ID is missing");
          return;
        }
        if (!data.priceId) {
          toast.error("Price ID is missing");
          return;
        }
        handleCancelPrice();
        const payload = {
          shipmentId: resolvedShipmentId,
          priceId: data.priceId,
          ...(data.chargeType !== "Cash on Delivery" && data.feeItem
            ? { feeItem: data.feeItem }
            : {}),
          responsibleUser: data.responsibleUser || null,
          responsibleUserType: data.responsibleUserType,
          charge: data.charge,
          chargeType: data.chargeType,
          payer: data.payer,
          paymentLocation: data.paymentLocation,
          ...(data.chargeType === "Charge" ||
            data.chargeType === "Cash on Delivery"
            ? { paymentStatus: data.paymentStatus }
            : { discountStatus: data.discountStatus }),
        };
        await updatePrice(payload).unwrap();
        toast.success("Price successfully updated!");
      } catch (err) {
        console.error("Update price error:", err);
        toast.error(err?.data?.message ?? "Couldn't update price!");
      }
    },
    [shipmentId, ShipmentDetail?._id, updatePrice, handleCancelPrice]
  );

  const isFormDisabled = !shipmentId || loadingFeeTypes || loadingDiscountTypes;

  const handleSetSelectedPrice = useCallback(
    (price) => {
      if (isFormDisabled) {
        toast.error(
          "Cannot edit price: Shipment or fee/discount types not loaded"
        );
        return;
      }
      setSelectedPrice(price);
      setShowPriceForm(true);
    },
    [isFormDisabled, setSelectedPrice, setShowPriceForm]
  );
  // Extract credit limit and credit amount
  const creditLimit = ShipmentDetail?.vendor?.vendorLimits?.creditLimit;
  const creditAmount = ShipmentDetail?.balanceDetails?.totalUnpaidCharges;

  // Show warning if creditAmount >= creditLimit
  // useEffect(() => {
  //   if (ShipmentDetail?.vendor && creditAmount >= creditLimit) {
  //     toast.warning(
  //       `⚠ Credit limit exceeded: Rs. ${creditAmount} / ${creditLimit}`,
  //       { autoClose: 5000, style: { background: "#fff3cd", color: "#856404" } }
  //     );
  //   }
  // }, [creditLimit, creditAmount, ShipmentDetail?.vendor]);
  useEffect(() => {
    if (ShipmentDetail?.vendor && creditAmount >= creditLimit && !isVendor) {
      toast.warning(
        `⚠ Credit limit exceeded: Used: Rs. ${creditAmount} / Limit: ${creditLimit}`,
        { autoClose: 5000, style: { background: "#fff3cd", color: "#856404" } }
      );
    }
  }, [creditLimit, creditAmount, ShipmentDetail?.vendor, isVendor]);

  return (
    <>
      {/* Bulk pricing progress overlay */}
      {bulkInProgress && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(4px)",
            gap: 2,
          }}
        >
          <CircularProgress size={56} thickness={4} sx={{ color: "#fff" }} />
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: 600,
              letterSpacing: 0.5,
              textAlign: "center",
              px: 2,
            }}
          >
            Shipment Pricing in progress…
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
            Please wait while all prices are being saved
          </Typography>
        </Box>
      )}
      <Stack direction="row" spacing={1.5} justifyContent="flex-end" padding={3} alignItems="center">
        {/* Display credit information if vendor exists */}
        {(ShipmentDetail?.vendor?.vendorLimits?.creditLimit != null && !isVendor) && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              border: "1px solid",
              display: "inline-block",
              backgroundColor: (theme) =>
                creditAmount >= creditLimit
                  ? theme.palette.mode === "dark"
                    ? "#2a0000"
                    : "#fff5f5"
                  : theme.palette.mode === "dark"
                    ? "#002a1a"
                    : "#f0fdf4",
              borderColor: (theme) =>
                creditAmount >= creditLimit ? theme.palette.error.main : theme.palette.success.main,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: (theme) =>
                  creditAmount >= creditLimit
                    ? theme.palette.error.main
                    : theme.palette.success.main,
                fontWeight: 500,
              }}
            >
              Credit Limit: Rs. {creditLimit?.toLocaleString() || "0"} | Used Amount: Rs.{" "}
              {creditAmount?.toLocaleString() || "0"}
            </Typography>
          </Box>
        )}



        {!canEdit && (
          <Button
            startIcon={<Add />}

            variant="outlined"
            onClick={() => {
              if (isFormDisabled) {
                toast.error(
                  "Cannot add price: Shipment or fee/discount types not loaded"
                );
                return;
              }
              setSelectedPrice(null);
              setShowPriceForm(true);
            }}
            disabled={ShipmentDetail?.status === "Delivered" || isFormDisabled || showPriceForm || (ShipmentDetail?.isActive === false)}
            size="small"
            sx={{ textTransform: "none" }}
          >
            Add Pricing
          </Button>
        )}
        {isAccountAdmin && (eBills.length > 0 || creditNotes.length > 0) && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintOutlined />}
              endIcon={<ArrowDropDown />}
              onClick={(event) => setPrintAnchor(event.currentTarget)}
              disabled={printingEBill || printingStoredEBill || printingCreditNote}
              sx={{ textTransform: "none", px: 1.75, py: 0.75, borderRadius: 1.5 }}
            >
              Print e-Bill / Credit Note ({eBills.length + creditNotes.length})
            </Button>
            <Menu
              anchorEl={printAnchor}
              open={Boolean(printAnchor)}
              onClose={() => setPrintAnchor(null)}
              PaperProps={{
                sx: {
                  mt: 1,
                  p: 0.75,
                  minWidth: 260,
                  borderRadius: 1.5,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
                },
              }}
            >
              {eBills.map((bill, index) => (
                <MenuItem
                  key={bill.eBillId}
                  onClick={() =>
                    bill.storedEBill
                      ? handlePrintStoredEBill(bill.eBillId)
                      : handlePrintEBill(bill._id)
                  }
                  sx={{ px: 1.5, py: 1, mb: index === eBills.length - 1 ? 0 : 0.5, borderRadius: 1 }}
                >
                  <ListItemText
                    primary={`Print ${bill.payer || "Shipment"} e-Bill`}
                    secondary={bill.eBillInvoiceNo || `e-Bill ${index + 1}`}
                  />
                </MenuItem>
              ))}
              {creditNotes.map((creditNote, index) => (
                <MenuItem
                  key={creditNote.creditNoteId}
                  onClick={() => handlePrintCreditNote(creditNote)}
                  sx={{ px: 1.5, py: 1, mb: index === creditNotes.length - 1 ? 0 : 0.5, borderRadius: 1 }}
                >
                  <ListItemText
                    primary={`Print ${creditNote.payer || "Shipment"} credit note`}
                    secondary={creditNote.creditNoteCode || creditNote.creditNoteId}
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {/* <Button
          startIcon={<Refresh />}
          variant="outlined"
          onClick={refreshPrices}
          disabled={isFormDisabled || loadingPrices || isFetching}
          size="small"
          sx={{ textTransform: "none" }}
        >
          Refresh Prices
        </Button> */}
      </Stack>
      {isFormDisabled && !shipmentId ? (
        <Box display="flex" justifyContent="center" padding={3}>
          <Typography variant="body1" color="textSecondary">
            No shipment selected
          </Typography>
        </Box>
      ) : loadingFeeTypes || loadingDiscountTypes ? (
        <Box display="flex" justifyContent="center" padding={3}>
          <CircularProgress size={24} />
          <Typography variant="body2" marginLeft={1}>
            Loading fee and discount types...
          </Typography>
        </Box>
      ) : (
        <ShipmentPricingList setSelectedPrice={handleSetSelectedPrice} />
      )}
      <FormDrawer
        title={selectedPrice ? "Edit Price" : "Add Price"}
        open={showPriceForm}
        onClose={handleCancelPrice}
        width={850}
      >
        <ShipmentPricingForm
          isBusy={addingPrice || updatingPrice || bulkInProgress}
          price={selectedPrice}
          shipmentId={shipmentId}
          onCancel={handleCancelPrice}
          onSubmit={selectedPrice ? handleUpdatePrice : handleAddPrice}
          feeTypeList={_mappedFeeTypeList}
          discountTypeList={_mappedDiscountTypeList}
        />
      </FormDrawer>
    </>
  );
};

export default ShipmentPricesTab;
